
import Page from '@/Page.vue';
import { Component, Ref, Watch } from 'vue-property-decorator';
import { CycleTimeModel, CycleTimeTypeEnum, ProductCollectionModel } from '@/libs/Api';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import { Guid } from '@/libs/common/functions/guid';
import { cycleTimesStore } from '@/libs/cycle-times/+state/store';
import { productCollectionsStore } from '@/libs/products/+state/store';
import Loader from '@/libs/common/components/Loader.vue';
import LocationSelect from '@/libs/common/components-smart/LocationSelect.vue';
import WorkplaceSelect from '@/libs/common/components-smart/WorkplaceSelect.vue';
import ProductSelect from '@/libs/common/components-smart/ProductSelect.vue';

@Component({
    components: { Loader, LocationSelect, WorkplaceSelect, ProductSelect },
})
export default class CycleTimeDialog extends Page {
    id!: string;
    dialog = false;
    valid = true;
    saving = false;
    loading = false;
    cycleTime: CycleTimeModel | null = null;
    timePerUnitSeconds: number | null = null;
    deleteDialog = false;
    deleting = false;

    @Ref() form!: any;

    get isNewItem(): boolean {
        return this.cycleTime?.id == Guid.EmptyGuid();
    }

    get cycleTimeTypes() {
        return [
            { text: this.$t('cycleTimes.type.byWorkplace') as any, value: CycleTimeTypeEnum.ByWorkplace },
            { text: this.$t('cycleTimes.type.byProductAndWorkplace') as any, value: CycleTimeTypeEnum.ByProductAndWorkplace },
            {
                text: this.$t('cycleTimes.type.byProductCollectionAndWorkplace') as any,
                value: CycleTimeTypeEnum.ByProductCollectionAndWorkplace,
            },
            { text: this.$t('cycleTimes.type.byProductAndLocation') as any, value: CycleTimeTypeEnum.ByProductAndLocation },
            {
                text: this.$t('cycleTimes.type.byProductCollectionAndLocation') as any,
                value: CycleTimeTypeEnum.ByProductCollectionAndLocation,
            },
        ];
    }

    get requiresLocation() {
        const t = this.cycleTime?.cycleTimeType;
        return t === CycleTimeTypeEnum.ByProductAndLocation || t === CycleTimeTypeEnum.ByProductCollectionAndLocation;
    }

    get requiresWorkplace() {
        const t = this.cycleTime?.cycleTimeType;
        return (
            t === CycleTimeTypeEnum.ByWorkplace ||
            t === CycleTimeTypeEnum.ByProductAndWorkplace ||
            t === CycleTimeTypeEnum.ByProductCollectionAndWorkplace
        );
    }

    get requiresProduct() {
        const t = this.cycleTime?.cycleTimeType;
        return t === CycleTimeTypeEnum.ByProductAndWorkplace || t === CycleTimeTypeEnum.ByProductAndLocation;
    }

    get requiresProductCollection() {
        const t = this.cycleTime?.cycleTimeType;
        return (
            t === CycleTimeTypeEnum.ByProductCollectionAndWorkplace || t === CycleTimeTypeEnum.ByProductCollectionAndLocation
        );
    }

    get headerTitle() {
        return this.$t('cycleTimes.edit') as any;
    }

    requiredRule(v: any) {
        return !!v || (this.$t('validation.requiredField') as any);
    }

    requiredNumberPositiveRule(v: any) {
        return (!!v && Number(v) > 0) || (this.$t('validation.enterAnInteger') as any);
    }

    get productCollections() {
        const items = (productCollectionsStore.useGetter(CrudGetter.Data) as any[]) ?? [];
        return items.map((x) => ({ id: x.id, label: `${x.name ?? ''}`.trim() })).sort((a, b) => a.label.localeCompare(b.label));
    }

    open(id: string) {
        this.dialog = true;
        this.id = id;
        this.load();
    }

    close() {
        this.dialog = false;
    }

    @Watch('cycleTime.cycleTimeType')
    typeChanged() {
        if (!this.cycleTime) return;
        // Clear fields not applicable for selected type to satisfy validation request
        if (!this.requiresLocation) this.cycleTime.locationId = null;
        if (!this.requiresWorkplace) this.cycleTime.workplaceId = null;
        if (!this.requiresProduct) this.cycleTime.productId = null;
        if (!this.requiresProductCollection) this.cycleTime.productCollectionId = null;
    }

    private load(): Promise<any> {
        this.loading = true;
        const loaders: Promise<any>[] = [];
        // Load dependencies
        loaders.push(this.loadProductCollections());

        if (this.id == Guid.EmptyGuid()) {
            this.cycleTime = { id: Guid.EmptyGuid(), timePerUnit: null } as CycleTimeModel;
            this.timePerUnitSeconds = null;
            this.loading = false;
            return Promise.resolve();
        } else {
            cycleTimesStore.dispatch(CrudAction.Get, { id: this.id });
            const p = this.subscribe(cycleTimesStore, CrudReponse.Get)
                .then((e: CycleTimeModel) => {
                    this.cycleTime = { ...e };
                    this.timePerUnitSeconds = this.parseSeconds(this.cycleTime.timePerUnit);
                    this.loading = false;
                })
                .catch((e: any) => (this.loading = false));
            loaders.push(p);
        }
        return Promise.all(loaders) as any;
    }

    private loadProductCollections(): Promise<any> {
        return new Promise((resolve, reject) => {
            productCollectionsStore.dispatch(
                CrudAction.GetAll,
                createCrudQueryPayload<ProductCollectionModel>([{ field: 'name', index: 1, order: 'asc' }])
            );
            this.subscribe(productCollectionsStore, CrudReponse.GetAll)
                .then(() => resolve(null))
                .catch((e: any) => reject(e));
        });
    }

    private save() {
        if (this.form.validate()) {
            const model = { ...this.cycleTime } as CycleTimeModel;
            // Ensure only allowed fields are set
            if (!this.requiresLocation) model.locationId = null;
            if (!this.requiresWorkplace) model.workplaceId = null;
            if (!this.requiresProduct) model.productId = null;
            if (!this.requiresProductCollection) model.productCollectionId = null;
            model.timePerUnit = this.formatSecondsToTimeSpan(this.timePerUnitSeconds);

            this.saving = true;
            if (this.id == Guid.EmptyGuid()) {
                cycleTimesStore.dispatch(CrudAction.Create, { item: model });
                this.subscribe(cycleTimesStore, CrudReponse.Create)
                    .then((e: CycleTimeModel) => {
                        this.cycleTime = e;
                        this.saving = false;
                        this.close();
                    })
                    .catch((e) => {
                        this.saving = false;
                        throw e;
                    });
            } else {
                cycleTimesStore.dispatch(CrudAction.Update, { item: model });
                this.subscribe(cycleTimesStore, CrudReponse.Update)
                    .then(() => {
                        this.saving = false;
                        this.close();
                    })
                    .catch((e) => {
                        this.saving = false;
                        throw e;
                    });
            }
        }
    }

    private parseSeconds(ct?: string | null): number | null {
        if (!ct) return null;
        const s = String(ct).trim();
        if (/^P/i.test(s)) {
            const m = /P(?:\d+D)?T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:[.,]\d+)?)S)?/i.exec(s);
            if (!m) return null;
            const hh = Number(m[1] || 0);
            const mm = Number(m[2] || 0);
            const ss = Number((m[3] || '0').replace(',', '.'));
            const total = hh * 3600 + mm * 60 + ss;
            return total > 0 ? total : null;
        }
        // d.hh:mm:ss[.fff] TimeSpan "c" formát
        const md = /^(\d+)\.(\d{1,2}):(\d{2}):(\d{2})(?:[.,](\d+))?$/.exec(s);
        if (md) {
            const d = Number(md[1]);
            const hh = Number(md[2]);
            const mm = Number(md[3]);
            const ss = Number(md[4]) + Number(md[5] ? '0.' + md[5] : 0);
            const total = d * 86400 + hh * 3600 + mm * 60 + ss;
            return total > 0 ? total : null;
        }
        const m = /^(\d{1,2}):(\d{2}):(\d{2})(?:[.,](\d+))?$/.exec(s);
        if (!m) return null;
        const hh = Number(m[1]);
        const mm = Number(m[2]);
        const ss = Number(m[3]) + Number(m[4] ? '0.' + m[4] : 0);
        const total = hh * 3600 + mm * 60 + ss;
        return total > 0 ? total : null;
    }

    private formatSecondsToTimeSpan(seconds: number | null): string | null {
        if (!seconds || Number(seconds) <= 0) return null;
        const s = Math.floor(Number(seconds));
        const days = Math.floor(s / 86400);
        const rem = s % 86400;
        const hh = Math.floor(rem / 3600);
        const mm = Math.floor((rem % 3600) / 60);
        const sec = rem % 60;
        const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
        const time = `${pad(hh)}:${pad(mm)}:${pad(sec)}`;
        return days > 0 ? `${days}.${time}` : time;
    }

    deleteClick() {
        this.deleteDialog = true;
    }

    deleteCycleTime() {
        this.deleting = true;
        cycleTimesStore.dispatch(CrudAction.Delete, { id: this.id });
        this.subscribe(cycleTimesStore, CrudReponse.Delete)
            .then(() => {
                this.deleting = false;
                this.deleteDialog = false;
                this.close();
            })
            .catch(() => {
                this.deleting = false;
            });
    }
}
